import React, { useEffect, useRef, useState } from 'react';
import { Music, X } from 'lucide-react';
import { logger } from '../lib/logger';
import { trackClick } from '../lib/track';

/**
 * MusicNavTrigger + MusicCenterModal (Phase 30 → Phase 34)
 *
 * The trigger lives in the top navbar and opens a centered Spotify-embed
 * modal. It is NEVER auto-opened and never plays audio without an explicit
 * user click on the embed.
 *
 * Phase 34 discoverability upgrades:
 *   1. **Label** — primary "Playlists" word + "Get in the Zone" kicker so
 *      first-time visitors immediately recognise this as a music feature
 *      (not a brand-only slogan).
 *   2. **First-visit pulse ring** — the icon emits a subtle cyan pulse ring
 *      for the first three page-views (until the user has either opened it
 *      or seen it enough times). Stored in localStorage so we don't pester
 *      returning visitors.
 *   3. **Click telemetry** — fires a Tealium `music_trigger_click` event so
 *      we can measure the impact of the discoverability changes in
 *      /admin/dashboards/analytics.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const PULSE_STORAGE_KEY = 'apex_music_trigger_pulse_views_v1';
const PULSE_VIEW_BUDGET = 3; // first N pageviews show the pulse ring

const MusicCenterModal = ({ open, onClose }) => {
  const [playlists, setPlaylists] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/playlists`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const items = Array.isArray(data?.playlists) ? data.playlists : [];
        if (!cancelled) {
          setPlaylists(items);
          setActiveIndex(0);
        }
      } catch (err) {
        logger.warn('MusicCenterModal playlist load failed', err);
        if (!cancelled) setPlaylists([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const active = playlists && playlists[activeIndex];

  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center bg-black/65 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="music-center-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Apex playlists"
    >
      <div className="w-full max-w-md bg-background rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-4 sm:px-5 py-3 border-b border-border/60 flex items-center justify-between">
          <div>
            <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-[#4FBCC7] font-bold">
              Get in the Zone
            </div>
            <div className="font-heading text-[15px] font-semibold mt-0.5">
              {active ? active.title : 'Apex Playlists'}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close music player"
            className="p-2 rounded-lg hover:bg-secondary/60"
            data-testid="music-center-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {playlists === null && (
          <div className="p-8 text-center text-[13px] text-foreground/55">Loading playlists…</div>
        )}
        {playlists !== null && playlists.length === 0 && (
          <div className="p-8 text-center text-[13px] text-foreground/55">No playlists available.</div>
        )}

        {active && (
          <>
            <div className="px-4 sm:px-5 py-3 border-b border-border/60 flex items-center justify-center gap-1.5 overflow-x-auto">
              {playlists.map((p, i) => (
                <button
                  key={p.id || p.spotify_id || i}
                  onClick={() => setActiveIndex(i)}
                  className={`px-2.5 h-7 rounded-md text-[11px] font-bold uppercase tracking-[0.14em] whitespace-nowrap transition-colors ${
                    i === activeIndex
                      ? 'bg-[#0a0d0e] text-white'
                      : 'bg-secondary/60 text-foreground/65 hover:bg-secondary'
                  }`}
                  data-testid={`music-center-tab-${p.id || i}`}
                >
                  {p.short_label || p.title || `Playlist ${i + 1}`}
                </button>
              ))}
            </div>
            <div className="p-3">
              <iframe
                title={active.title || 'Apex playlist'}
                src={`https://open.spotify.com/embed/playlist/${active.spotify_id}?utm_source=apex&theme=0`}
                width="100%"
                height="360"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                className="rounded-xl border-0"
              />
              {active.clean_verified && (
                <div className="text-[10.5px] uppercase tracking-[0.18em] font-mono-accent text-foreground/55 mt-2 text-center">
                  Clean lyrics verified
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Lazily read the pulse view counter from localStorage. Returns the number of
// pageviews already credited toward the pulse budget. Guarded for SSR /
// private-browsing modes that block localStorage.
function readPulseViewsSoFar() {
  try {
    if (typeof window === 'undefined') return 0;
    const raw = window.localStorage.getItem(PULSE_STORAGE_KEY);
    if (!raw) return 0;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

export const MusicNavTrigger = ({ className = '', scrolled = false }) => {
  const [open, setOpen] = useState(false);
  // Show the pulse ring if the user hasn't seen the trigger enough times yet
  // AND hasn't opened it. Once they open it, the pulse retires forever.
  const [showPulse, setShowPulse] = useState(
    () => readPulseViewsSoFar() < PULSE_VIEW_BUDGET,
  );
  const btnRef = useRef(null);

  // Increment the pageview counter once on mount. If we just hit the budget,
  // also turn the pulse off so it doesn't flash on the next render. The
  // setState inside the effect is the legitimate output of this counter
  // bookkeeping — disable the React 19 compiler advisory.
  
  useEffect(() => {
    try {
      const next = readPulseViewsSoFar() + 1;
      window.localStorage.setItem(PULSE_STORAGE_KEY, String(next));
      if (next > PULSE_VIEW_BUDGET) setShowPulse(false);
    } catch (err) {
      // Best-effort persistence — localStorage may be disabled (Safari
      // private mode, storage quota). Surface in dev tools for visibility.
      if (typeof console !== 'undefined' && console.debug) {
        console.debug('[MusicNavTrigger] pulse counter persist failed', err);
      }
    }
  }, []);

  const handleClick = () => {
    setOpen(true);
    if (showPulse) setShowPulse(false);
    // Mark the pulse fully exhausted once the user has interacted, so the
    // pulse never reappears on future visits even if their view counter is
    // still under budget.
    try {
      window.localStorage.setItem(
        PULSE_STORAGE_KEY,
        String(PULSE_VIEW_BUDGET + 1),
      );
    } catch (err) {
      if (typeof console !== 'undefined' && console.debug) {
        console.debug('[MusicNavTrigger] pulse persist failed', err);
      }
    }
    trackClick('music_trigger_click', {
      component: 'navbar',
      surface: scrolled ? 'scrolled' : 'top',
    });
  };

  const colorClasses = scrolled
    ? 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
    : 'text-white/85 hover:text-white hover:bg-white/10';

  return (
    <>
      {/* Local keyframes for the discoverability pulse. transform/opacity only
          (perf + design-guideline rule). Respects prefers-reduced-motion. */}
      <style>{`
        @keyframes apex-music-ring-pulse {
          0%   { transform: scale(0.85); opacity: 0.55; }
          70%  { transform: scale(1.55); opacity: 0;    }
          100% { transform: scale(1.55); opacity: 0;    }
        }
        @keyframes apex-music-icon-bob {
          0%, 100% { transform: translateY(0)   rotate(0deg);  }
          25%      { transform: translateY(-1px) rotate(-3deg); }
          75%      { transform: translateY(1px)  rotate(3deg);  }
        }
        .apex-music-ring {
          animation: apex-music-ring-pulse 2.2s cubic-bezier(0.22, 1, 0.36, 1) infinite;
        }
        .apex-music-bob {
          animation: apex-music-icon-bob 2.6s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .apex-music-ring, .apex-music-bob { animation: none !important; }
        }
      `}</style>

      <button
        ref={btnRef}
        type="button"
        onClick={handleClick}
        aria-label="Open Apex playlists — Get in the Zone"
        data-testid="music-nav-trigger"
        className={`relative group inline-flex items-center gap-1.5 rounded-md px-2.5 h-9 transition-colors duration-150 ${colorClasses} ${className}`}
      >
        {/* Pulse ring — only painted for the first PULSE_VIEW_BUDGET visits. */}
        {showPulse && (
          <span
            aria-hidden="true"
            className="apex-music-ring pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full"
            style={{
              boxShadow: '0 0 0 2px rgba(79, 188, 199, 0.55)',
            }}
            data-testid="music-nav-trigger-pulse"
          />
        )}
        <Music
          className={`h-[18px] w-[18px] relative ${showPulse ? 'apex-music-bob' : 'group-hover:animate-pulse'}`}
          strokeWidth={showPulse ? 2.5 : 2}
        />
        <span className="text-[13px] font-semibold whitespace-nowrap">
          Playlists
        </span>
        <span
          className={`hidden lg:inline font-mono-accent uppercase tracking-[0.16em] text-[9.5px] font-bold ${
            scrolled ? 'text-[#4FBCC7]' : 'text-[#4FBCC7]'
          }`}
          aria-hidden="true"
        >
          · Get in the Zone
        </span>
      </button>
      <MusicCenterModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default MusicNavTrigger;
